import React, { useMemo } from 'react';
import { getSceneCubeMapDir, getSceneType } from '@wsc/utils/sceneUtils';
import { get3DModelUrl, getBustKey } from '@wsc/utils/urlHelpers';
import { useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { preLoadConnectedScenes } from 'threejs_scene/sceneUtils';
import { getSceneObjects } from '@wsc/apis/webStoreAPI';
import { useEffect } from 'react';
import { formURL } from '@wsc/utils/apiUtils';
import { useLocalize } from '@wsc/hooks';
import { selectSceneBgLoaded } from '@wsc/redux_stores/sceneLoadReducer';

const selectRoomObjects = createSelector(
	(state) => state.roomObjects,
	(roomObjects) => roomObjects,
);

const ConnectedScenesPreLoader = ({ webpSupport }) => {
	/**
	 * PreLoad any assets required for the next scene here
	 * **/
	const roomObjects = useSelector(selectRoomObjects);
	const abortControl = new AbortController();
	const scenes = useSelector((state) => state.scenes);
	const { localesEnabled, activeLocale } = useLocalize();
	const sceneBgLoaded = useSelector(selectSceneBgLoaded);

	const getSceneAssets = (scene) => {
		const { scene_type } = scene;
		const result = {};
		if (scene_type === 'cubemap_scene') {
			result.cube_map_dir = getSceneCubeMapDir(
				scene,
				localesEnabled,
				activeLocale,
			);
		}

		if (scene_type === '3d_model_scene') {
			if (scene?.scene_background)
				result.scene_background = formURL(scene.scene_background);
			if (scene?.environment_map_config?.url)
				result.scene_background = formURL(
					scene.environment_map_config.url,
				);
			if (scene?.scene_model?.url)
				result.scene_model = get3DModelUrl(scene);
		}
		return result;
	};

	const linkedScenes = useMemo(() => {
		return Object.values(roomObjects)
			.filter((item) => item.type === 'NavMarker')
			.map((item) => scenes[item?.props?.linked_room_id?.$oid])
			.filter(
				(item) =>
					item &&
					['cubemap_scene', '3d_model_scene'].includes(
						getSceneType(item),
					),
			)
			.map((item) => ({
				sceneId: item?.id,
				sceneType: item.scene_type,
				imageIntegrity: getBustKey(item),
				useWebp: webpSupport,
				...getSceneAssets(item),
			}));
	}, [roomObjects, scenes, webpSupport]);

	const preLoadConnectedScenesRoomObjects = async (linkedScenes) =>
		Promise.all(
			linkedScenes.map((item) =>
				getSceneObjects(item.sceneId, activeLocale, true, abortControl),
			),
		);

	const preLoad = () => {
		preLoadConnectedScenes(linkedScenes, abortControl);
		preLoadConnectedScenesRoomObjects(linkedScenes).catch((err) => {
			if (err?.name === 'AbortError') {
				console.log(
					'%c Aborted preloading of connected scenes',
					'color:yellow',
				);
			} else {
				console.error('Error preloading connected scenes', err);
			}
		});
	};

	useEffect(() => {
		if (sceneBgLoaded) {
			preLoad();
		}
		return () => {
			abortControl.abort();
		};
	}, [sceneBgLoaded, roomObjects]);

	return <></>;
};
export default ConnectedScenesPreLoader;
